import type { ComponentType } from 'react';
import { AlertTriangle, Calendar, ClipboardList, FileText, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import type { AssistantPrompt } from '../types';
import styles from '../styles/AIAssistant.module.css';
import { ScrollArea, Stack, Text } from '../ui/primitives.js';
import { getAssistantMotionTransition } from './internal/assistantMotion';

export type AIAssistantSuggestedPromptsProps = {
  prompts: AssistantPrompt[];
  title?: string;
  disabled?: boolean;
  onPromptClick: (prompt: AssistantPrompt) => void;
};

const resolveIcon = (
  prompt: AssistantPrompt
): ComponentType<{ size?: number | string; strokeWidth?: number | string }> => {
  if (prompt.icon) {
    return prompt.icon;
  }
  const text = prompt.label.toLowerCase();
  if (text.includes('deadline') || text.includes('interview')) {
    return Calendar;
  }
  if (text.includes('document') || text.includes('upload')) {
    return FileText;
  }
  if (text.includes('flag') || text.includes('priority')) {
    return AlertTriangle;
  }
  if (text.includes('status') || text.includes('summary')) {
    return ClipboardList;
  }
  return Sparkles;
};

export function AIAssistantSuggestedPrompts({
  prompts,
  title = 'Suggested prompts',
  disabled = false,
  onPromptClick,
}: AIAssistantSuggestedPromptsProps) {
  const reducedMotion = Boolean(useReducedMotion());

  return (
    <ScrollArea className={styles.suggestedQuestions} data-testid="ai-suggested-questions">
      <Stack gap="xs">
        <Text size="xs" className={styles.suggestedTitle}>
          {title}
        </Text>
        {prompts.map((prompt, index) => {
          const Icon = resolveIcon(prompt);
          const badge = prompt.badge || (prompt.isDynamic ? 'Smart' : undefined);

          return (
            <motion.div
              key={prompt.id ?? prompt.label}
              initial={reducedMotion ? { opacity: 0 } : { opacity: 0, x: -10 }}
              animate={reducedMotion ? { opacity: 1 } : { opacity: 1, x: 0 }}
              transition={{
                ...getAssistantMotionTransition('micro', reducedMotion),
                delay: reducedMotion ? 0 : index * 0.04,
              }}
            >
              <button
                className={styles.suggestedQuestion}
                onClick={() => onPromptClick(prompt)}
                disabled={disabled}
                data-testid={`suggested-question-${index}`}
                data-dynamic={prompt.isDynamic}
                type="button"
              >
                <span className={styles.suggestedIcon}>
                  <Icon size={14} />
                </span>
                <span className={styles.suggestedLabel}>{prompt.label}</span>
                {badge ? (
                  <span className={styles.suggestedBadge}>{badge}</span>
                ) : null}
              </button>
            </motion.div>
          );
        })}
      </Stack>
    </ScrollArea>
  );
}
